import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { brandColors } from '@config/brandTokens';
import type { ContestCatalogEntry } from '../../types/contestCatalog';
import {
  CONTEST_AGE_CATEGORIES,
  formatContestVideoNumber,
  getMediaForSection,
  getVisibleFrame2Sections,
  resolveMediaClipForAge,
} from '@utils/contestCatalog';
import { ContestEmbeddedVideo } from '@components/contest/PublicContestFrames';

/** Seed 2–3 ages around the primary so Compare videos opens with content immediately. */
function seedVideoCompareAges(primaryAge: string): string[] {
  const ages = [...CONTEST_AGE_CATEGORIES];
  const idx = Math.max(0, ages.indexOf(primaryAge as (typeof ages)[number]));
  if (idx < 0) return ages.slice(0, 3);
  const start = Math.max(0, Math.min(idx - 1, ages.length - 3));
  return ages.slice(start, start + 3);
}

const MAX_COMPARE_AGES = 3;

interface ContestVideoCompareDialogProps {
  open: boolean;
  onClose: () => void;
  contest: ContestCatalogEntry | null;
  primaryAge: string;
}

const ContestVideoCompareDialog: React.FC<ContestVideoCompareDialogProps> = ({
  open,
  onClose,
  contest,
  primaryAge,
}) => {
  const [compareAges, setCompareAges] = useState<string[]>(() => seedVideoCompareAges(primaryAge));

  useEffect(() => {
    if (open) setCompareAges(seedVideoCompareAges(primaryAge));
  }, [open, primaryAge, contest?.id]);

  const sectionRows = useMemo(() => {
    if (!contest) return [];
    return getVisibleFrame2Sections(contest)
      .map((meta) => ({
        key: meta.key,
        label: `${formatContestVideoNumber(meta.visibleIndex)} ${meta.label}`,
        visibleIndex: meta.visibleIndex,
        videos: getMediaForSection(contest, meta.key),
      }))
      .filter((row) => row.videos.length > 0);
  }, [contest]);

  const toggleAge = (age: string) => {
    setCompareAges((prev) => {
      if (prev.includes(age)) {
        if (prev.length <= 1) return prev;
        return prev.filter((a) => a !== age);
      }
      if (prev.length >= MAX_COMPARE_AGES) {
        return [...prev.slice(1), age];
      }
      return [...prev, age];
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: { xs: '100%', md: '96vw' },
          maxWidth: 1280,
          height: { xs: '100%', md: '90vh' },
          m: { xs: 0, md: 2 },
          borderRadius: { xs: 0, md: 2 },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          pr: 1,
          borderBottom: '1px solid #e2e8f0',
          fontFamily: '"Sora", Arial, sans-serif',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, color: brandColors.navy, fontSize: '1.1rem' }}>
            Compare videos{contest ? ` — ${contest.title}` : ''}
          </Typography>
          <Typography sx={{ color: brandColors.slate, fontSize: '0.85rem', mt: 0.35 }}>
            Select age groups to see the performance and growth and required skill level
          </Typography>
        </Box>
        <IconButton aria-label="Close" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Stack
          direction="row"
          spacing={0.75}
          useFlexGap
          flexWrap="wrap"
          sx={{ px: 2, py: 1.25, borderBottom: '1px solid #e2e8f0', bgcolor: brandColors.ice }}
        >
          <Typography
            sx={{
              fontSize: '0.72rem',
              fontWeight: 800,
              color: brandColors.slate,
              textTransform: 'uppercase',
              letterSpacing: 0.4,
              alignSelf: 'center',
              mr: 0.5,
            }}
          >
            Ages
          </Typography>
          {CONTEST_AGE_CATEGORIES.map((age) => {
            const active = compareAges.includes(age);
            return (
              <Chip
                key={age}
                size="small"
                label={age}
                onClick={() => toggleAge(age)}
                sx={{
                  height: 26,
                  fontWeight: active ? 800 : 600,
                  bgcolor: active ? brandColors.navy : brandColors.white,
                  color: active ? brandColors.white : brandColors.navy,
                  border: `1px solid ${active ? brandColors.navy : '#cbd5e1'}`,
                }}
              />
            );
          })}
        </Stack>

        {!contest ? (
          <Box sx={{ p: 3 }}>
            <Typography color="text.secondary">Select a skill first to compare videos.</Typography>
          </Box>
        ) : sectionRows.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <Typography color="text.secondary">No learning videos published for this skill yet.</Typography>
          </Box>
        ) : (
          <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
            <Table
              size="small"
              stickyHeader
              sx={{ minWidth: 320 + compareAges.length * 280, borderCollapse: 'separate' }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 800,
                      bgcolor: '#f1f5f9',
                      color: brandColors.navy,
                      position: 'sticky',
                      left: 0,
                      zIndex: 3,
                      minWidth: 150,
                      borderRight: '1px solid #e2e8f0',
                    }}
                  >
                    Section
                  </TableCell>
                  {compareAges.map((age) => (
                    <TableCell
                      key={age}
                      align="center"
                      sx={{
                        fontWeight: 800,
                        bgcolor: '#f1f5f9',
                        color: brandColors.navy,
                        minWidth: 260,
                      }}
                    >
                      {age}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sectionRows.map((section) => (
                  <TableRow key={section.key} hover>
                    <TableCell
                      sx={{
                        fontWeight: 800,
                        color: brandColors.navy,
                        verticalAlign: 'top',
                        position: 'sticky',
                        left: 0,
                        bgcolor: brandColors.white,
                        zIndex: 1,
                        borderRight: '1px solid #e2e8f0',
                      }}
                    >
                      {section.label}
                    </TableCell>
                    {compareAges.map((age) => (
                      <TableCell key={`${section.key}-${age}`} sx={{ verticalAlign: 'top', bgcolor: brandColors.ice }}>
                        <Stack spacing={1.25}>
                          {section.videos.map((video, index) => {
                            const numberLabel = formatContestVideoNumber(section.visibleIndex + index);
                            const clip = resolveMediaClipForAge(video, age);
                            const related = clip.relatedVideos || [];
                            return (
                              <Box
                                key={`${video.id}-${age}`}
                                sx={{
                                  borderRadius: 1.5,
                                  overflow: 'hidden',
                                  border: '1px solid #e2e8f0',
                                  bgcolor: brandColors.white,
                                }}
                              >
                                <Box sx={{ position: 'relative' }}>
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: 6,
                                      left: 6,
                                      zIndex: 1,
                                      minWidth: 22,
                                      height: 22,
                                      px: 0.5,
                                      borderRadius: '999px',
                                      bgcolor: brandColors.blue,
                                      color: '#fff',
                                      fontWeight: 900,
                                      fontSize: '0.68rem',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    {numberLabel}
                                  </Box>
                                  <ContestEmbeddedVideo url={clip.url} title={clip.title} />
                                </Box>
                                <Box sx={{ px: 0.85, py: 0.55, borderTop: '1px solid #f1f5f9' }}>
                                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: brandColors.navy }}>
                                    {numberLabel} {clip.title}
                                  </Typography>
                                </Box>
                                {related.map((rel, relIndex) => {
                                  const relatedNumber = formatContestVideoNumber(section.visibleIndex + index, relIndex);
                                  const relClip = resolveMediaClipForAge(
                                    {
                                      title: rel.title,
                                      url: rel.url,
                                      notes: rel.notes,
                                      byAge: rel.byAge,
                                    },
                                    age,
                                  );
                                  return (
                                    <Box
                                      key={rel.id}
                                      sx={{
                                        borderTop: '1px solid #e2e8f0',
                                        bgcolor: '#f8fafc',
                                      }}
                                    >
                                      <Box sx={{ position: 'relative' }}>
                                        <Box
                                          sx={{
                                            position: 'absolute',
                                            top: 6,
                                            left: 6,
                                            zIndex: 1,
                                            px: 0.5,
                                            height: 18,
                                            borderRadius: '999px',
                                            bgcolor: '#a78bfa',
                                            color: '#fff',
                                            fontWeight: 900,
                                            fontSize: '0.6rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                          }}
                                        >
                                          {relatedNumber}
                                        </Box>
                                        <ContestEmbeddedVideo url={relClip.url} title={relClip.title} />
                                      </Box>
                                      <Box sx={{ px: 0.75, py: 0.45 }}>
                                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#334155' }}>
                                          {relatedNumber} {relClip.title}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  );
                                })}
                              </Box>
                            );
                          })}
                        </Stack>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.25, borderTop: '1px solid #e2e8f0' }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 700 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContestVideoCompareDialog;
